"use client";

import { useState, useEffect, useCallback } from "react";
import { ActiveTab } from "../../lib/types";

export interface UseNavbarProps {
    activeTab: ActiveTab;
    showFloatingNav: boolean;
    lastSaved: Date | null;
    isSaving: boolean;
    saveError: string | null;
    onChangeActiveTab: (tab: ActiveTab) => void;
    onToggleFloatingNav: (show: boolean) => void;
}

export interface UseNavbarReturn {
    sidebarOpen: boolean;
    showFloatingNav: boolean;
    showSuccessMessage: boolean;
    toggleSidebar: () => void;
    toggleFloatingNav: () => void;
}

/**
 * Custom hook for navbar functionality
 * Handles sidebar toggle and save status display
 */
export const useNavbar = ({
    activeTab,
    showFloatingNav,
    lastSaved,
    isSaving,
    saveError,
    onChangeActiveTab,
    onToggleFloatingNav,
}: UseNavbarProps): UseNavbarReturn => {
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    const sidebarOpen = activeTab !== "closed";

    const toggleSidebar = useCallback(() => {
        const tab = sidebarOpen ? "closed" : "scenes";
        onChangeActiveTab(tab);
    }, [sidebarOpen, onChangeActiveTab]);

    const toggleFloatingNav = useCallback(() => {
        onToggleFloatingNav(!showFloatingNav);
    }, [showFloatingNav, onToggleFloatingNav]);

    // Show success message when save completes
    useEffect(() => {
        if (lastSaved && !isSaving && !saveError) {
            setShowSuccessMessage(true);
            const timer = setTimeout(() => {
                setShowSuccessMessage(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [lastSaved, isSaving, saveError]);

    return {
        sidebarOpen,
        showFloatingNav,
        showSuccessMessage,
        toggleSidebar,
        toggleFloatingNav,
    };
};
